import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./TrackSection.module.css";
import FiltersBar from "../components/FiltersBar";
import KpiCard from "../components/KpiCard";
import EvolutionChart from "../components/EvolutionChart";
import TopContentTable, { TopContentColumn, TopContentRow } from "../components/TopContentTable";
import NationalityList from "../components/NationalityList";
import ExportButton from "../components/ExportButton";
import { useTrackAnalytics } from "../hooks/useTrackAnalytics";
import { useExportCsv } from "../hooks/useExportCsv";
import { getProducts } from "../../../api";
import { AudiotracksCMS } from "../../../types/types";
import { SelectOption } from "../types";
import { formatDecimal, formatDurationMinutes, formatIntegerOrDash, formatPercent } from "../utils/formatNumber";
import { fillDailyGaps, startOfDay, endOfDay } from "../utils/deriveDateBuckets";
import AppDownloadsBlock from "../components/AppDownloadsBlock";
import DateFilterBar from "../components/DateFilterBar";
import ProductFilterBar from "../components/ProductFilterBar";

const PRIMARY = "#86e3ce";
const TRACK_MAX_DATE_RANGE_DAYS = 366;

interface TrackSectionProps {
    isOwner: boolean;
}

const TrackSection: React.FC<TrackSectionProps> = ({ isOwner }) => {
    const { t } = useTranslation();
    const tt = t as unknown as (k: string) => string;

    const [products, setProducts] = useState<AudiotracksCMS[]>([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
    const [selectedTrackIds, setSelectedTrackIds] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    /** Snapshot of the (ids, range) used by the last successful fetch. Drives
     *  the chart's `fillDailyGaps`, AND is reused as the source of truth for
     *  the CSV export so the export can never desync from the displayed
     *  data (e.g. user clears the selection after fetching). */
    const [appliedQuery, setAppliedQuery] = useState<{
        ids: string[];
        start: Date;
        end: Date;
    } | null>(null);

    const { data, loading, fetch, reset } = useTrackAnalytics();
    const { exporting, run: runExport } = useExportCsv();

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;
            setProductsLoading(true);
            try {
                const res = await getProducts({ token });
                if (!cancelled && Array.isArray(res)) {
                    setProducts(res);
                }
            } catch {
                // surfacing this in the dropdown is enough; we leave the list empty.
            } finally {
                if (!cancelled) setProductsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const companyOptions = useMemo<SelectOption[]>(() => {
        const byId = new Map<string, string>();
        for (const p of products) {
            for (const c of p.companies || []) {
                if (c.id) {
                    byId.set(c.id, c.name || c.id);
                }
            }
        }
        return Array.from(byId.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    }, [products]);

    const showCompanyFilter = isOwner && companyOptions.length > 1;

    const productsForTrackPicker = useMemo(() => {
        const withTrack = products.filter((p) => Boolean(p.trackId));
        if (!isOwner || !showCompanyFilter) {
            return withTrack;
        }
        if (selectedCompanyIds.length === 0) {
            return withTrack;
        }
        return withTrack.filter((p) => (p.companies || []).some((c) => selectedCompanyIds.includes(c.id)));
    }, [products, isOwner, showCompanyFilter, selectedCompanyIds]);

    const options = useMemo<SelectOption[]>(
        () => productsForTrackPicker.map((p) => ({ value: p.trackId, label: p.name || p.trackId })),
        [productsForTrackPicker],
    );

    useEffect(() => {
        const allowed = new Set(productsForTrackPicker.map((p) => p.trackId));
        setSelectedTrackIds((ids) => ids.filter((id) => allowed.has(id)));
    }, [productsForTrackPicker]);

    const trackIdsResolvedForApply = useMemo(() => {
        if (selectedTrackIds.length > 0) {
            return Array.from(new Set(selectedTrackIds));
        }
        const ids = productsForTrackPicker.map((p) => p.trackId).filter(Boolean) as string[];
        return Array.from(new Set(ids));
    }, [selectedTrackIds, productsForTrackPicker]);

    const canApply = trackIdsResolvedForApply.length > 0 && Boolean(startDate) && Boolean(endDate);

    const handleApply = async () => {
        if (!canApply || !startDate || !endDate) return;
        const start = startOfDay(startDate);
        const end = endOfDay(endDate);
        const ids = trackIdsResolvedForApply.slice();
        await fetch({ trackIds: ids, startDate: start, endDate: end });
        setAppliedQuery({ ids, start, end });
    };

    const handleReset = () => {
        setSelectedCompanyIds([]);
        setSelectedTrackIds([]);
        setStartDate(null);
        setEndDate(null);
        setAppliedQuery(null);
        reset();
    };

    // --- Owner layout: period and product filters are applied independently. ---

    /** Period applies first; it drives BOTH the download block and the track
     *  KPIs, fetching with whatever product selection is currently resolved. */
    const canApplyDates = Boolean(startDate) && Boolean(endDate);
    const handleApplyDates = async () => {
        if (!canApplyDates || !startDate || !endDate) return;
        const start = startOfDay(startDate);
        const end = endOfDay(endDate);
        const ids = trackIdsResolvedForApply.slice();
        await fetch({ trackIds: ids, startDate: start, endDate: end });
        setAppliedQuery({ ids, start, end });
    };

    /** Products re-fetch the track KPIs only, reusing the already-applied
     *  period — so it is disabled until a period has been applied. */
    const canApplyProducts = Boolean(appliedQuery) && trackIdsResolvedForApply.length > 0;
    const handleApplyProducts = async () => {
        if (!appliedQuery) return;
        const ids = trackIdsResolvedForApply.slice();
        await fetch({ trackIds: ids, startDate: appliedQuery.start, endDate: appliedQuery.end });
        setAppliedQuery({ ids, start: appliedQuery.start, end: appliedQuery.end });
    };

    /** Reset for the period bar also clears the downstream product results. */
    const handleResetDates = () => {
        setStartDate(null);
        setEndDate(null);
        setAppliedQuery(null);
        reset();
    };

    const handleResetProducts = () => {
        setSelectedCompanyIds([]);
        setSelectedTrackIds([]);
    };

    // Downloads (and the split-filter layout) show for the single-app variants
    // (colorbus/vdp/thoiry — every admin) and for owners on Rewind (default).
    // Non-owner admins on Rewind keep the legacy combined-filter layout.
    // const showDownloads = getCmsVariant() !== "default" || isOwner;
    const showDownloads = isOwner;

    const handleExport = () => {
        if (!appliedQuery || appliedQuery.ids.length === 0) return;
        runExport({
            tab: "tracks",
            ids: appliedQuery.ids,
            startDate: appliedQuery.start,
            endDate: appliedQuery.end,
        });
    };

    const series = useMemo(() => {
        if (!data || !appliedQuery) return [];
        return fillDailyGaps(appliedQuery.start, appliedQuery.end, data.series, () => ({ uniqueStarters: 0 }));
    }, [data, appliedQuery]);

    const topContentRows: TopContentRow[] = useMemo(() => {
        if (!data) return [];
        return data.topContents.map((c) => ({
            id: c.trackId,
            label: c.name,
            cells: { starts: c.starts, percentage: c.percentage },
        }));
    }, [data]);

    const topContentsScope = data?.topContentsScope ?? "track";
    const topContentLabels = (() => {
        switch (topContentsScope) {
            case "waypoint":
                return {
                    title: tt("topPointsOfInterest"),
                    subtitle: undefined,
                    help: tt("topPointsOfInterestHelp"),
                    nameCol: tt("topContentName"),
                    countCol: tt("trackVisitors"),
                    percentCol: tt("percentOfVisitors"),
                };
            case "clickableZone":
                return {
                    title: tt("topClickableZones"),
                    subtitle: undefined,
                    help: tt("topClickableZonesHelp"),
                    nameCol: tt("topContentName"),
                    countCol: tt("trackVisitors"),
                    percentCol: tt("percentOfVisitors"),
                };
            case "track":
            default:
                return {
                    title: tt("topExperiences"),
                    subtitle: undefined,
                    help: tt("topExperiencesHelp"),
                    nameCol: tt("topContentName"),
                    countCol: tt("trackVisitors"),
                    percentCol: tt("percentOfVisitors"),
                };
        }
    })();

    const topContentColumns: TopContentColumn[] = [
        { key: "name", label: topContentLabels.nameCol },
        { key: "starts", label: topContentLabels.countCol, numeric: true },
        { key: "percentage", label: topContentLabels.percentCol, numeric: true, bar: true },
    ];

    const engagement = data?.engagement;
    const percentTour = engagement?.percentTourListened ?? null;
    const avgPoiEngaged = engagement?.avgPoiPerEngagedUser ?? null;
    const avgListen = engagement?.avgListenTime ?? null;

    const companyFilterConfig = showCompanyFilter
        ? {
              placeholder: tt("analyticsFilterCompanyPlaceholder"),
              options: companyOptions,
              selectedIds: selectedCompanyIds,
              onSelectedIdsChange: setSelectedCompanyIds,
              loading: productsLoading,
          }
        : null;

    // Visitor/track KPIs + chart + tables — identical in both layouts.
    const resultsBlock = (
        <>
            <div className={styles.headerRow}>
                <ExportButton
                    label={tt("exportCsv")}
                    loadingLabel={tt("exporting")}
                    onClick={handleExport}
                    disabled={!data || !appliedQuery || appliedQuery.ids.length === 0}
                    loading={exporting}
                />
            </div>

            <div className={styles.kpiGrid}>
                <KpiCard
                    title={tt("uniqueStarters")}
                    value={formatIntegerOrDash(data?.uniqueStarters)}
                    help={tt("uniqueStartersHelp")}
                />
                <KpiCard
                    title={tt("avgListenTime")}
                    value={formatDurationMinutes(avgListen)}
                    help={tt("avgListenTimeHelp")}
                />
                <KpiCard
                    title={tt("avgPoiPerEngagedUser")}
                    value={avgPoiEngaged !== null ? formatDecimal(avgPoiEngaged, 1) : "—"}
                    help={tt("avgPoiPerEngagedUserHelp")}
                />
                <KpiCard
                    title={tt("percentTourListened")}
                    value={percentTour !== null ? formatPercent(percentTour, 0) : "—"}
                    help={tt("percentTourListenedHelp")}
                />
            </div>

            <EvolutionChart
                title={tt("evolutionOfUniqueStarters")}
                subtitle={tt("evolutionOfUniqueStartersSubtitle")}
                data={series}
                seriesDefs={[{ key: "uniqueStarters", label: tt("uniqueStarters"), color: PRIMARY }]}
                emptyLabel={tt("noDataInRange")}
            />

            <div className={styles.twoCol}>
                <TopContentTable
                    title={topContentLabels.title}
                    subtitle={topContentLabels.subtitle}
                    help={topContentLabels.help}
                    columns={topContentColumns}
                    rows={topContentRows}
                    emptyLabel={tt("noDataInRange")}
                    barIsPercentage
                />
                <NationalityList
                    title={tt("languagesUsed")}
                    data={data?.byLanguage || []}
                    emptyLabel={tt("noDataInRange")}
                />
            </div>
        </>
    );

    // Owner layout: period (drives everything) → app downloads → product
    // filters (drive only the KPIs below). Two independent Apply buttons.
    if (showDownloads) {
        return (
            <div className={styles.root}>
                <DateFilterBar
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                    maxRangeDays={TRACK_MAX_DATE_RANGE_DAYS}
                    canApply={canApplyDates}
                    applying={loading}
                    onApply={handleApplyDates}
                    onReset={handleResetDates}
                />

                <AppDownloadsBlock start={appliedQuery?.start ?? null} end={appliedQuery?.end ?? null} />

                <ProductFilterBar
                    selectorPlaceholder={tt("allProductsPlaceholder")}
                    options={options}
                    selectedIds={selectedTrackIds}
                    onSelectedIdsChange={setSelectedTrackIds}
                    optionsLoading={productsLoading}
                    companyFilter={companyFilterConfig}
                    canApply={canApplyProducts}
                    applying={loading}
                    onApply={handleApplyProducts}
                    onReset={handleResetProducts}
                />

                {resultsBlock}
            </div>
        );
    }

    // Legacy layout (non-owner): single combined filter bar, one Apply.
    return (
        <div className={styles.root}>
            <FiltersBar
                selectorPlaceholder={tt("allProductsPlaceholder")}
                options={options}
                selectedIds={selectedTrackIds}
                onSelectedIdsChange={setSelectedTrackIds}
                optionsLoading={productsLoading}
                companyFilter={companyFilterConfig}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                maxRangeDays={TRACK_MAX_DATE_RANGE_DAYS}
                canApply={canApply}
                applying={loading}
                onApply={handleApply}
                onReset={handleReset}
            />

            {resultsBlock}
        </div>
    );
};

export default TrackSection;
